import { zodResolver } from '@hookform/resolvers/zod'
import { Layers } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Select } from '@/components/ui/Select'
import { CLASS_CYCLES, type ClassCycle } from '@/features/school/vocabulary'
import { tAny } from '@/i18n'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Input } from '@/components/ui/Input'
import { useSaveClass, type ClassInput } from '../api'
import type { SchoolClass } from '../types'
import { FormDialog } from './FormDialog'

const optionalAge = z
  .string()
  .trim()
  .refine((v) => v === '' || (/^\d+$/.test(v) && Number(v) <= 30), 'Enter an age between 0 and 30.')
  .transform((v) => (v === '' ? null : Number(v)))

const classSchema = z
  .object({
    name: z.string().trim().min(1, 'Enter the class name.').max(50, 'Use at most 50 characters.'),
    level: z
      .string()
      .trim()
      .regex(/^\d+$/, 'Enter a whole number.')
      .transform(Number)
      .refine((n) => n <= 99, 'Use a level between 0 and 99.'),
    minAge: optionalAge,
    maxAge: optionalAge,
    cycle: z.string().transform((v) => (v === '' ? null : (v as ClassCycle))),
    series: z
      .string()
      .trim()
      .max(30, 'Use at most 30 characters.')
      .transform((v) => (v === '' ? null : v)),
  })
  .refine((v) => v.minAge === null || v.maxAge === null || v.maxAge >= v.minAge, {
    path: ['maxAge'],
    message: 'Must be at least the minimum age.',
  })

type ClassFormInput = z.input<typeof classSchema>
const FIELDS = ['name', 'level', 'minAge', 'maxAge', 'cycle', 'series'] as const
const FORM_ID = 'class-form'

interface ClassDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  schoolClass?: SchoolClass | null
  /** Suggested level for a new class (e.g. highest existing + 1). */
  nextLevel?: number
  onSaved?: (saved: SchoolClass) => void
}

export function ClassDialog({ open, onOpenChange, schoolClass, nextLevel = 1, onSaved }: ClassDialogProps) {
  const { t } = useTranslation('academic')
  const save = useSaveClass()
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<Layers />}
      title={schoolClass ? t('Edit {{name}}', { name: schoolClass.name }) : t('New class')}
      description={t('A class is a grade or year group; students are placed in its sections.')}
      formId={FORM_ID}
      submitLabel={schoolClass ? t('Save changes') : t('Create class')}
      pending={save.isPending}
    >
      <ClassForm
        defaults={{
          name: schoolClass?.name ?? '',
          level: String(schoolClass?.level ?? nextLevel),
          minAge: schoolClass?.minAge?.toString() ?? '',
          maxAge: schoolClass?.maxAge?.toString() ?? '',
          cycle: schoolClass?.cycle ?? '',
          series: schoolClass?.series ?? '',
        }}
        onSubmit={async (values) => {
          const saved = await save.mutateAsync({ ...values, id: schoolClass?.id })
          toast.success(schoolClass ? t('Class updated') : t('Class created'), saved.name)
          onOpenChange(false)
          onSaved?.(saved)
        }}
      />
    </FormDialog>
  )
}

function ClassForm({
  defaults,
  onSubmit,
}: {
  defaults: ClassFormInput
  onSubmit: (values: ClassInput) => Promise<void>
}) {
  const { t } = useTranslation('academic')
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ClassFormInput, unknown, ClassInput>({
    resolver: zodResolver(classSchema),
    defaultValues: defaults,
  })

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values)
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  return (
    <form id={FORM_ID} onSubmit={(e) => void submit(e)} noValidate className="grid gap-4 sm:grid-cols-2">
      <FormField label={t('Name')} error={errors.name?.message} required>
        <Input placeholder={t('Grade 1')} autoComplete="off" {...register('name')} />
      </FormField>
      <FormField
        label={t('Level')}
        error={errors.level?.message}
        required
        hint={t('Orders classes and drives promotion.')}
      >
        <Input type="number" inputMode="numeric" min={0} max={99} {...register('level')} />
      </FormField>
      <FormField label={t('Minimum age')} error={errors.minAge?.message}>
        <Input
          type="number"
          inputMode="numeric"
          min={0}
          max={30}
          placeholder={t('Any')}
          {...register('minAge')}
        />
      </FormField>
      <FormField label={t('Maximum age')} error={errors.maxAge?.message}>
        <Input
          type="number"
          inputMode="numeric"
          min={0}
          max={30}
          placeholder={t('Any')}
          {...register('maxAge')}
        />
      </FormField>
      <FormField label={t('Cycle')} hint={t('For schools with several stages (optional).')}>
        <Select
          placeholder={t('No cycle')}
          options={CLASS_CYCLES.map((c) => ({ value: c.value, label: tAny(c.label) }))}
          {...register('cycle')}
        />
      </FormField>
      <FormField
        label={t('Series')}
        error={errors.series?.message}
        hint={t('For classes split by série (L, S, OSE…). Leave empty otherwise.')}
      >
        <Input placeholder="L" autoComplete="off" maxLength={30} {...register('series')} />
      </FormField>
    </form>
  )
}
